import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { RootState } from 'store';

import AnimatedModal from 'components/AnimatedModal';
import useIsInsideApp from 'hooks/useIsInsideApp';
import useStoreTranslation from 'hooks/useStoreTranslation';
import MENU_ENTRIES, { MenuEntry, MenuKey, MenuRoles, MenuDisplay } from 'utils/menuEntries';
import { goBackToApp } from 'utils/webViewUtils';

import NavigationList from './NavigationList';
import RequesterProfile from './RequesterProfile';
import { Container, Icon, Logout, Subtitle } from './styles';
import useCompanyConfiguration from 'hooks/useCompanyConfiguration';
export interface Props {
  isAdmin: boolean;
  isApprover: boolean;
  isTravelArranger: boolean;
  isSuperAdmin: boolean;
  fullName: string;
  email: string;
  show: boolean;
  onClose: () => void;
  onLogout: () => Promise<any>;
}

const UserMenu = ({
  show,
  fullName,
  email,
  isAdmin,
  isApprover,
  isTravelArranger,
  isSuperAdmin,
  onClose,
  onLogout,
}: Props) => {
  const history = useHistory();
  const { t } = useStoreTranslation();
  const isInsideApp = useIsInsideApp();
  const accountResult = useSelector((state: RootState) => state.user.accountResult);

  const companyName = useMemo(() => accountResult?.companyName || '', [accountResult]);
  const companyCountry = useMemo(() => accountResult?.countryName || '', [accountResult]);
  const companyCountryCode = useMemo(() => accountResult?.countryId || '', [accountResult]);
  const hasCompanyBranches = useMemo(() => accountResult?.hasBranchCompanies || false, [accountResult]);
  const branchCompanyLogged = useMemo(() => accountResult?.branchCompanyLogged || false, [accountResult]);
  const companyConfiguration = useCompanyConfiguration();

  const handleEntryClick = (entry: MenuEntry) => {
    switch (entry.key) {
      case MenuKey.Dashboard:
      case MenuKey.Booking:
        history.push(entry.mainRoute);
        onClose();
        break;
      case MenuKey.WhatsApp:
        window.open(
          `https://api.whatsapp.com/send?phone=${companyConfiguration?.menuView?.useWhatsappSupport}&text=${companyConfiguration?.menuView?.useWhatsappSupportMessage}`,
        );
        break;
      default:
        if (companyConfiguration?.menuView?.downloadExcelExpenses) {
          
          window.open('/corporate/FORMATO_REINTEGRO_VIAJES_Y_GASTOS_AL_EXTERIOR.xlsx', '_blank');
              
          if (isAdmin || isSuperAdmin) {
              history.push('/corporate/dashboard');
            }
            else {
              history.push('/corporate/booking');
            }

        }
        else {
          history.push(entry.mainRoute);
        }
        break;
    }
  };

  const handleLogout = () => {
    if (isInsideApp) {
      goBackToApp();
      return;
    }

    onLogout().then(() => history.push('/corporate/login'));
  };

  const showMenu = (menuKey: MenuKey) => 
  {
    // Expenses = 3
    if (menuKey === MenuKey.Expenses && companyConfiguration?.menuView?.hideExpenses) {
      return false;
    }

    // CostCenter = 9
    if (menuKey === MenuKey.CostCenter && companyConfiguration?.menuView?.hideCostCenter) {
      return false;
    }

    // Report = 10
    if (menuKey === MenuKey.Report && companyConfiguration?.menuView?.hideReports) {
      return false;
    }

    return true;
  };

  const showWhatsApp = (menuKey: MenuKey) =>
    companyConfiguration?.menuView?.showWhatsappAllPages &&
    companyConfiguration?.menuView?.useWhatsappSupport !== '' &&
    companyConfiguration?.menuView?.useWhatsappSupport !== null &&
    companyConfiguration?.menuView?.useWhatsappSupport !== undefined;

    return (
    <AnimatedModal show={show} onClose={onClose}>
      <Container>
        <RequesterProfile
          name={fullName}
          email={email}
          onClose={onClose}
          companyName={companyName}
          countryName={companyCountry}
          branchCompanyLogged={branchCompanyLogged}
          hasCompanyBranches={hasCompanyBranches}
          countryCode={companyCountryCode}
        />
        <NavigationList
          list={MENU_ENTRIES.filter((entry) => entry.display.includes(MenuDisplay.Mobile)).map((entry) => ({
            icon: <Icon small={entry.iconSmall} src={entry.iconSrc} />,
            name: t(entry.name),
            detail: t(entry.detail),
            onClick: () => handleEntryClick(entry),
            show: entry.key !== MenuKey.WhatsApp ? entry.roles.includes(MenuRoles.Traveler) : showWhatsApp(entry.key),
          }))}
        />

        {(isAdmin || isApprover || isSuperAdmin) && <Subtitle>{t('userMenu.administrator')}</Subtitle>}

        <NavigationList
          list={MENU_ENTRIES.filter((entry) => entry.display.includes(MenuDisplay.Mobile)).map((entry) => ({
            icon: <Icon small={entry.iconSmall} src={entry.iconSrc} />,
            name: t(entry.name),
            detail: t(entry.detail),
            onClick: () => handleEntryClick(entry),
            show:
            (showMenu(entry.key) &&
            ((entry.roles.includes(MenuRoles.Admin) && isAdmin) ||
              (entry.roles.includes(MenuRoles.Approver) && isApprover) ||
              (entry.roles.includes(MenuRoles.TravelArranger) && isTravelArranger) ||
              (entry.roles.includes(MenuRoles.SuperAdmin) && isSuperAdmin)))             
          }))}
        />
        <Logout onClick={handleLogout}>{t('userMenu.logout')}</Logout>
      </Container>
    </AnimatedModal>
  );
};

export default UserMenu;
